"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { SearchForm } from "@/components/SearchForm";
import { AnalysisProgress } from "@/components/AnalysisProgress";
import { OverallScore } from "@/components/OverallScore";
import { MiniScoreCharts } from "@/components/MiniScoreCharts";
import { PageSpeedOverview } from "@/components/PageSpeed/PageSpeedOverview";
import SiteMetadataOverview from "@/components/PageSpeed/SiteMetadataOverview";
import { TrustSecurityOverview } from "@/components/PageSpeed/TrustSecurityOverview";
import { analyzeResponseSchema, type AnalyzeData } from "@/types/analyze";
import type { SiteMetadataSummary } from "@/lib/site-analysis";
import { ShareImageButton } from "@/components/ShareImageButton";
import { useSession } from "next-auth/react";
import { SaveReportSignInDialog } from "@/components/SaveReportSignInDialog";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import UnsavedReportsDialog from "@/components/UnsavedReportsDialog";
import { ClearReportButton } from "@/components/ClearReportButton";
import config from "@/config";
import { FREE_ANALYSIS_COMPLETED_EVENT } from "@/components/AnalysisQuotaCard";

// Local storage helpers for unsaved anonymous reports
const UNSAVED_REPORTS_STORAGE_KEY = "unsavedReports";
const PREFILL_STORAGE_KEY = "analysisPrefillUrl";

interface UnsavedReportEntry {
  id: string;
  createdAt: string; // ISO date
}

const saveUnsavedReportId = (reportId: string, createdAtIso: string) => {
  try {
    // Ensure storage is available
    if (typeof window === "undefined" || !window.localStorage) return;
    const raw = window.localStorage.getItem(UNSAVED_REPORTS_STORAGE_KEY);
    const existing: UnsavedReportEntry[] = raw ? JSON.parse(raw) : [];

    // Dedupe (keep all, newest first; no TTL, no cap)
    const withoutDup = existing.filter((e) => e.id !== reportId);
    const next: UnsavedReportEntry[] = [
      { id: reportId, createdAt: createdAtIso },
      ...withoutDup,
    ].slice(0, 100);

    window.localStorage.setItem(
      UNSAVED_REPORTS_STORAGE_KEY,
      JSON.stringify(next)
    );
  } catch {
    // ignore storage errors
  }
};

interface WebsiteAnalysisProps {
  className?: string;
  reportIdFromUrl?: string | undefined;
  mode?: "marketing" | "app";
  initialUrl?: string;
  freeTierLimit?: number | null;
  freeAnalysesUsed?: number;
  llmsPrompt?: string | null;
}

const normalizeInputUrl = (input: string): string => {
  const trimmed = input.trim();
  if (!trimmed) return "";
  if (!/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  if (trimmed.startsWith("http://")) {
    return trimmed.replace("http://", "https://");
  }
  return trimmed;
};

export function WebsiteAnalysis({
  className = "",
  reportIdFromUrl,
  mode = "marketing",
  initialUrl,
  freeTierLimit,
  freeAnalysesUsed = 0,
  llmsPrompt = null,
}: WebsiteAnalysisProps) {
  const { data: session } = useSession();
  const router = useRouter();
  const isAppMode = mode === "app";
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [analysisData, setAnalysisData] = useState<AnalyzeData | null>(null);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [unsavedCount, setUnsavedCount] = useState<number>(0);
  const [justCompletedAnalysis, setJustCompletedAnalysis] = useState(false);
  const hasPaidAccess = Boolean(session?.user?.hasPaidAccess);

  const loadReportFromId = useCallback(
    async (reportId: string) => {
      if (!isAppMode) return;
      try {
        const response = await fetch(`/api/analyze?reportId=${reportId}`);
        const text = await response.text();
        const json = text ? JSON.parse(text) : null;
        const parsed = analyzeResponseSchema.safeParse(json);

        if (parsed.success) {
          const resp = parsed.data;
          if (resp.success && resp.data) {
            setAnalysisData(resp.data);
            toast.success("Report loaded successfully");
          } else {
            toast.error("Report not found or analysis failed");
          }
        } else {
          console.error("❌ Parse Error:", parsed.error);
          toast.error("Failed to load report data");
        }
      } catch (error) {
        console.error("[report] Failed to load report:", error);
        toast.error("Failed to load report. Please try again.");
      }
    },
    [isAppMode]
  );

  const handleAnalyze = (url: string) => {
    const normalizedUrl = normalizeInputUrl(url);

    if (!isAppMode) {
      const prefillUrl = `/analysis?prefill=${encodeURIComponent(normalizedUrl)}`;
      router.push(`/signin?callbackUrl=${encodeURIComponent(prefillUrl)}`);
      return;
    }

    setIsAnalyzing(true);
    setIsDone(false);
    setFormError(null);

    // Call unified orchestrator endpoint and store raw data result as-is
    void (async () => {
      const headers = { "Content-Type": "application/json" } as const;
      try {
        const res = await fetch("/api/analyze", {
          method: "POST",
          headers,
          body: JSON.stringify({ url: normalizedUrl }),
        });
        const text = await res.text();
        const json = text ? JSON.parse(text) : null;
        const parsed = analyzeResponseSchema.safeParse(json);
        if (parsed.success) {
          const resp = parsed.data;
          if (resp.success && resp.data) {
            setAnalysisData(resp.data);

            // Mark that we just completed an analysis (for upgrade popup logic)
            const isCompleted = resp.data.status === "completed";
            const isFreeUser =
              typeof freeTierLimit === "number" && freeTierLimit > 0;

            if (isCompleted && isFreeUser) {
              setJustCompletedAnalysis(true);
            }

            if (
              isAppMode &&
              typeof window !== "undefined" &&
              typeof freeTierLimit === "number" &&
              freeTierLimit > 0
            ) {
              window.dispatchEvent(new Event(FREE_ANALYSIS_COMPLETED_EVENT));
            }
            // Update URL with report ID and trigger navigation so Server Components see searchParams
            try {
              const newReportId = resp.data.report?.id;
              if (newReportId && typeof window !== "undefined") {
                const current = new URL(window.location.href);
                current.searchParams.set("report", newReportId);
                router.replace(`${current.pathname}${current.search}`);
              } else {
                router.refresh();
              }
            } catch {
              router.refresh();
            }

            // If report is completed and anonymous, store reportId locally for later attach
            try {
              const reportId = resp.data.report?.id;
              const reportCreatedAt = resp.data.report?.createdAt;
              const isCompleted = resp.data.status === "completed";
              const isAnonymous = (resp.data.userId ?? null) === null;
              if (reportId && reportCreatedAt && isCompleted && isAnonymous) {
                saveUnsavedReportId(reportId, reportCreatedAt);
              }
            } catch {
              // ignore storage errors
            }

            // Track analyze goal only on successful analysis
            try {
              window?.datafast?.("report_created");
            } catch {
              // ignore tracking errors
            }
          } else {
            // Handle API errors
            const errorMessage = resp.error || "Analysis failed";
            setFormError(errorMessage);
          }
        } else {
          console.error("❌ Parse Error:", parsed.error);
          const errorMessage = "Invalid response from server";
          setFormError(errorMessage);
        }
      } catch (error) {
        console.error("[analyze] /api/analyze request failed:", error);
        const errorMessage = "Network error. Please check your connection.";
        setFormError(errorMessage);
      }
      // Signal completion to progress component (let AnalysisProgress finish)
      setIsDone(true);
    })();
  };

  const handleAnalysisComplete = (): void => {
    setIsAnalyzing(false);
  };

  // Load report from URL parameter passed via props
  const currentReportId = analysisData?.report?.id ?? null;

  useEffect(() => {
    if (!isAppMode) return;
    const reportId = reportIdFromUrl ?? null;
    if (!reportId || reportId === currentReportId) {
      return;
    }
    loadReportFromId(reportId);
  }, [reportIdFromUrl, currentReportId, isAppMode, loadReportFromId]);

  // Reset form when results are shown
  useEffect(() => {
    if (!isAppMode) return;
    if (analysisData) {
      setResetTrigger((prev) => prev + 1);
    }
  }, [analysisData, isAppMode]);

  // Show save dialog 5s after completed anonymous report (dedup per report)
  useEffect(() => {
    if (!isAppMode) return;
    if (!analysisData) return;
    const isCompleted = analysisData.status === "completed";
    const isAnonymous = (analysisData.userId ?? null) === null;
    const isLoggedIn = Boolean(session?.user);
    const reportId = analysisData.report?.id ?? null;

    if (isCompleted && isAnonymous && !isLoggedIn && reportId) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [analysisData, session, isAppMode]);

  // After login: if there are unsaved reports in localStorage, show a popup
  useEffect(() => {
    if (!isAppMode) return;
    try {
      const isLoggedIn = Boolean(session?.user);
      if (!isLoggedIn) return;
      if (typeof window === "undefined" || !window.localStorage) return;

      const raw = window.localStorage.getItem(UNSAVED_REPORTS_STORAGE_KEY);
      const entries: UnsavedReportEntry[] = raw ? JSON.parse(raw) : [];
      const count = entries.length;
      if (count > 0) {
        setUnsavedCount(count);
        setShowUnsavedDialog(true);
      }
    } catch {
      // ignore storage errors
    }
  }, [session?.user, isAppMode]);

  // Show upgrade popup 20s after completing analysis (for free users)
  useEffect(() => {
    if (!justCompletedAnalysis) return;

    const timer = setTimeout(() => {
      setShowUpgradeDialog(true);
    }, 20000); // 20 seconds delay

    return () => clearTimeout(timer);
  }, [justCompletedAnalysis]);

  return (
    <div className={className}>
      {isAppMode && (
        <SaveReportSignInDialog
          open={showPrompt}
          onOpenChange={setShowPrompt}
        />
      )}
      {isAppMode && (
        <UnsavedReportsDialog
          open={showUnsavedDialog}
          onOpenChange={setShowUnsavedDialog}
          count={unsavedCount}
        />
      )}
      {/* SearchForm mit Text links daneben - beide zentriert */}
      <div className="relative max-w-2xl mx-auto">
        <SearchForm
          onAnalyze={handleAnalyze}
          disabled={isAppMode ? isAnalyzing : false}
          resetTrigger={isAppMode ? resetTrigger : 0}
          externalError={isAppMode ? formError : null}
          initialUrl={initialUrl}
          mode={mode}
          freeTierLimit={freeTierLimit}
          freeAnalysesUsed={freeAnalysesUsed}
          onUpgradeClick={() => setShowUpgradeDialog(true)}
        />
        {/* Text oben drüber (mobile) oder links daneben (desktop) - nur auf marketing page */}
        {!isAppMode && (
          <div className="absolute left-1/2 -translate-x-1/2 -top-10 text-center lg:left-0 lg:top-1/2 lg:-translate-y-1/2 lg:-translate-x-full lg:pr-4 text-sm text-foreground whitespace-nowrap">
            <span className="inline-block max-lg:[animation:slide-top-bottom_2s_ease-in-out_infinite] lg:[animation:slide-right-left_2s_ease-in-out_infinite]">
              Try for free <span className="lg:hidden">👇</span>
              <span className="hidden lg:inline">👉</span>
            </span>
          </div>
        )}
      </div>

      {/* Show loader during analysis */}
      {isAppMode && isAnalyzing && (
        <AnalysisProgress
          isVisible={isAnalyzing}
          onComplete={handleAnalysisComplete}
          waitForDone
          done={isDone}
        />
      )}

      {/* Show results when not analyzing */}
      {isAppMode && !isAnalyzing && analysisData && (
        <div className="mt-12 space-y-12">
          {/* Actions: Share + Clear */}
          <div className="mt-6 flex items-center justify-end gap-2">
            <ShareImageButton
              score={analysisData?.report.overallScore ?? 0}
              url={analysisData?.url ?? ""}
              createdAt={analysisData?.createdAt ?? new Date().toISOString()}
              pageSpeedScore={analysisData?.report.psiOverallScore ?? undefined}
              websiteQualityScore={
                analysisData?.report.siteAnalysisOverallScore ?? undefined
              }
              trustSecurityScore={
                analysisData?.report.trustSecurityOverallScore ?? undefined
              }
            />
            <ClearReportButton
              onCleared={() => {
                // Reset local analysis state so the page shows the default view again
                setAnalysisData(null);
                setIsAnalyzing(false);
                setIsDone(false);
                setFormError(null);
              }}
            />
          </div>

          <OverallScore
            score={analysisData.report.overallScore ?? 0}
            url={analysisData.url}
            createdAt={analysisData.createdAt}
          />

          <MiniScoreCharts analysisData={analysisData} />

          <SiteMetadataOverview
            siteMetadata={
              analysisData.siteMetadata as SiteMetadataSummary | null
            }
            defaultExpanded={false}
            hasPaidAccess={hasPaidAccess}
            onRequireUpgrade={() => setShowUpgradeDialog(true)}
            showCopyButton
            llmsPrompt={llmsPrompt}
          />

          <TrustSecurityOverview
            trustSecurity={analysisData.trustSecurity}
            defaultExpanded={false}
            hasPaidAccess={hasPaidAccess}
            onRequireUpgrade={() => setShowUpgradeDialog(true)}
            showCopyButton
          />

          <PageSpeedOverview
            pageSpeed={analysisData.pageSpeed}
            defaultExpanded={false}
            planType={analysisData.planType}
          />
        </div>
      )}

      {/* Dialogs */}
      <SaveReportSignInDialog open={showPrompt} onOpenChange={setShowPrompt} />

      <UnsavedReportsDialog
        open={showUnsavedDialog}
        onOpenChange={setShowUnsavedDialog}
        count={unsavedCount}
      />

      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        showMaybeLater={false}
      />
    </div>
  );
}
